/* global globalThis */

import React, { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import Dashboard from "../component/pages/Falcon360/dashboard/dashboard.js";
import Header from "../component/header/Header.jsx";
import Sidebar from "../component/sidebar/Sidebar.js";
import Footer from "../component/footer/Footer.js";
import AuditLogsTable from "../component/common/audit/AuditLogs.js";
import ErrorPage from "../component/common/ErrorPage.js";
import DataSource from "../component/pages/Falcon360/dataSource/DataSource.js";
import { ChatInterface } from "../ai-agent/ui/ChatInterface.js";
import { ChatModal } from "../ai-agent/ui/ChatModal.js";
import DatabaseConnectionsTable from "../component/pages/Falcon360/dataSource/DataConnections.js";
import ForecastingPage from "../component/pages/Falcon360/upload/ForecastingPage.js";
import PIIPageRefactored from "../component/pages/Falcon360/upload/PIIPage_Refactored.js";
import DataQualityDashboard from "../component/pages/Falcon360/upload/DataQuality.js";
import { LoaderCircle } from "lucide-react";
import axiosClient, { setAutoLogoutReset } from "../api-client/AxiosClient.js";
import { FalconAIChatbot } from "../ai-agent/ui/aiassistant.js";
import { Help } from "../component/pages/Falcon360/targetMapping/CreateTable.js";
import { useOrgConfigSync } from "../component/header/Orgconfigservice.js";
import { useMsal } from "@azure/msal-react";


const Layout = ({ isOpen, setIsOpen }) => {
  const { instance } = useMsal();
  const timerRef = useRef(null);
  const location = useLocation();

  useOrgConfigSync(); // Critical: Must be here to initiate permission sync before Header is rendered

  const logout = useCallback(() => {
    console.warn("Auto-logout triggered due to inactivity");
    sessionStorage.clear();
    localStorage.removeItem("organization_config_id");
    localStorage.removeItem("selectedCorporation");

    instance.logoutPopup().catch((e) => {
      console.error("Auto-logout failed:", e);
      globalThis.location.reload();
    });
  }, [instance]);

  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    // 30 minute timeout
    timerRef.current = setTimeout(logout, 30 * 60 * 1000);
  }, [logout]);

  useEffect(() => {
    setAutoLogoutReset(resetTimer);
    const activityEvents = ["mousedown", "keydown", "scroll", "touchstart", "mousemove", "click"];
    activityEvents.forEach((event) => globalThis.addEventListener(event, resetTimer));
    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      activityEvents.forEach((event) => globalThis.removeEventListener(event, resetTimer));
    };
  }, [resetTimer]);

  const [chatOpen, setChatOpen] = useState(false);
  const [chatMinimized, setChatMinimized] = useState(false);
  const [isRolesLoaded, setIsRolesLoaded] = useState(!localStorage.getItem("organization_config_id"));
  const [isUserGuideLoaded, setIsUserGuideLoaded] = useState(false);
  const [run, setRun] = useState(false);

  const isAgentRoute = location.pathname === "/ai-agent";
  const isViewDataRoute = location.pathname === "/view-data";
  const isHistoryPage = location.pathname.includes("/history-data");

  // Map product routes to their required permission keys
  const routePermissions = {
    "/dashboard": "OSDashboard",
    "/data-sources": "OSDataSource",
    "/data-sources/add-data-source": "OSDataSource",
    "/data-quality": "DQ",
    "/forecasting": "IssuePredicit",
    "/pii": "PII",
    "/ai-assistance": "AIAsst",
  };

  // Define base valid paths (always accessible if authenticated)
  const basePaths = ["/", "/profile", "/logs", "/error", "/help"];

  // Get permissions from storage for real-time validation
  const getPermissions = () => JSON.parse(sessionStorage.getItem("rolesAndPermission") || "{}");
  const permissions = getPermissions();

  const isPathAuthorized = (path) => {
    // 1. Check if it's a base path
    if (basePaths.includes(path) || path.startsWith("/history-data/")) return true;

    // 2. Check if it requires a specific permission
    const requiredPermission = routePermissions[path];
    if (requiredPermission) {
      return !!permissions[requiredPermission]?.canView;
    }

    // 3. Fallback for sub-routes or dynamic routes (e.g. data-sources/...)
    const parentPath = Object.keys(routePermissions).find(p => path.startsWith(p + "/"));
    if (parentPath) {
      return !!permissions[routePermissions[parentPath]]?.canView;
    }

    return true; // Default to true for unknown routes to avoid blocking new features
  };

  const getFallbackRoute = () => {
    // Find the first module the user HAS access to
    const firstAvailable = Object.entries(routePermissions).find(([path, key]) => permissions[key]?.canView);
    return firstAvailable ? firstAvailable[0] : "/help"; // Use /help as ultimate fallback
  };

  const fetchisUserGuideViewed = async () => {
    try {
      const response = await axiosClient.get("api/User/GuideViewStatus");
      setRun(response.data || false);
    } catch (error) {
      console.error("Failed to fetch guide status:", error);
    }
    setIsUserGuideLoaded(true);
  };

  useEffect(() => {
    const handlePermissionsUpdated = () => {
      setIsRolesLoaded(true);
    };

    globalThis.addEventListener("permissionsUpdated", handlePermissionsUpdated);

    // Initial check: if roles are already in session storage, unlock immediately
    if (sessionStorage.getItem("rolesAndPermission")) {
      setIsRolesLoaded(true);
    }

    return () => {
      globalThis.removeEventListener("permissionsUpdated", handlePermissionsUpdated);
    };
  }, []);

  useEffect(() => {
    fetchisUserGuideViewed();
  }, []);

  // Update authentication status when permissions change
  useEffect(() => {
    const handlePermissionsUpdated = () => {
      setIsRolesLoaded(true);
    };

    globalThis.addEventListener("permissionsUpdated", handlePermissionsUpdated);

    // Initial check: if roles are already in session storage, unlock immediately
    if (sessionStorage.getItem("rolesAndPermission")) {
      setIsRolesLoaded(true);
    }

    return () => globalThis.removeEventListener("permissionsUpdated", handlePermissionsUpdated);
  }, []);

  // Use permissions handle for route protection
  const isAuthorized = isPathAuthorized(location.pathname);

  // If path is invalid or unauthorized, redirect to the best fallback
  if (!isAuthorized && location.pathname !== "/") {
    const fallback = getFallbackRoute();
    return <Navigate to={fallback} replace />;
  }

  const closeChat = () => setChatOpen(false);
  const toggleMinimize = () => setChatMinimized((prev) => !prev);

  // Calculate content width based on route and sidebar state
  const getContentStyle = () => {
    let marginLeft = "0px";
    if (!isAgentRoute) {
      marginLeft = isOpen ? "0px" : "80px";
    }

    const baseStyle = {
      marginLeft,
      marginTop: "80px", // Add margin-top to account for fixed header
      padding: isAgentRoute ? "0px" : "10px",
      width: "100%",
      transition: "all 0.3s ease-in-out",
    };

    if (isHistoryPage) {
      return {
        ...baseStyle,
        overflow: "hidden",
        marginLeft: isOpen ? "0px" : "95px",
      };
    }

    // Special handling for view-data route (CreateTable component)
    if (isViewDataRoute) {
      return {
        ...baseStyle,
        maxWidth: isOpen ? "calc(100vw)" : "calc(100vw - 50px)",
        overflow: "hidden",
        marginLeft: isOpen ? "0px" : "95px",
      };
    }

    return baseStyle;
  };

  return (
    <>
      {isRolesLoaded && isUserGuideLoaded ? (
        <div
          className="app-wrapper"
          style={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Header
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            setRun={setRun}
            run={run}
          />

          <div
            className={`app-container ${isAgentRoute ? "h-screen" : ""}`}
            style={{ display: "flex", flex: 1 }}
          >
            {!isAgentRoute && <Sidebar isOpen={isOpen} />}

            <div className="content" style={getContentStyle()}>
              <Routes>
                <Route path="/dashboard" element={<Dashboard />} />
                <Route
                  path="/data-sources"
                  element={<DatabaseConnectionsTable />}
                />
                <Route path="/data-quality" element={<DataQualityDashboard />} />
                <Route path="/forecasting" element={<ForecastingPage />} />
                <Route path="/pii" element={<PIIPageRefactored />} />

                <Route path="/logs" element={<AuditLogsTable />} />
                <Route path="/error" element={<ErrorPage />} />

                <Route path="/ai-assistance" element={<FalconAIChatbot />} />

                <Route path="/help" element={<Help />} />
                <Route
                  path="/data-sources/add-data-source"
                  element={<DataSource />}
                />
                {/* Catch all invalid paths and redirect to dashboard */}
                <Route
                  path="*"
                  element={<Navigate to="/dashboard" replace />}
                />
              </Routes>
            </div>

            {/* Chat Modal */}
            <ChatModal
              open={chatOpen}
              minimized={chatMinimized}
              onClose={closeChat}
              onMinimize={toggleMinimize}
            >
              <ChatInterface />
            </ChatModal>
          </div>

          <Footer />
        </div>
      ) : (
        <div className="fixed inset-0 flex items-center justify-center bg-white bg-opacity-80 z-50">
          <LoaderCircle className="animate-spin text-blue-900" size={50} />
        </div>
      )}
    </>
  );
};

Layout.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
};

export default Layout;
